import React, { useCallback, useEffect, useState } from 'react'
import classnames from 'classnames'
import { intl } from 'lang/intl'
import { useSelector } from 'react-redux'
import { usePrevious } from 'react-use'
import { FormattedMessage } from 'react-intl'

import { TtButton as Button } from '@23/ui-react'

import { WebinarInfoSidebarComponent } from '../../../../components/WebinarInfoSidebarComponent/WebinarInfoSidebarComponent'
import HandoutsFeature from '../../../HandoutsFeature'
import { useWebinarColors } from '../../../../hooks/useWebinarColors'
import SessionsDrawer from '../../../WebinarSidebarFeature/components/WebinarSidebarComponent/SessionsDrawer/SessionsDrawer'
import { WebinarInfoAudienceSidebarComponent } from '../WebinarInfoAudienceSidebarComponent/WebinarInfoAudienceSidebarComponent'
import {
  WebinarPanelComponent,
  WebinarSidebarSecondaryPanelComponent,
} from '../../../WebinarSidebarFeature/components'
import { PollsFeature } from '../../..'
import { intToBool } from '../../../../utils'
import useFeatures from '../../../../hooks/useFeatures'
import { Chat } from '../../../../../Chat/components/Chat/Chat'
import { useActiveActionCardsQuery } from '../../../../../ActionCards/blocs/useActionCards'
import { ActionCard } from '../../../../../ActionCards/components/ActionCard'
import { useLiveDataQuery } from '../../../../../global/blocs/useLiveData'

import './WebinarAudienceSidebarComponent.scss'

interface WebinarAudienceMobileSidebarProps {
  isSecondaryPanelVisible: boolean
  toggleSecondaryPanel: (panel: null | 'sessions') => void
  onAskQuestion: () => void
  togglePollOverlay: () => void // TODO: type this
  isInGearMode: boolean
  secondaryPanelContentType: null | 'sessions'
}

type MobileDeviceTab = 'chat' | 'other' | 'sessions'

const WebinarAudienceMobileSidebarComponent = ({
  toggleSecondaryPanel,
  onAskQuestion,
  togglePollOverlay,
  isInGearMode,
  secondaryPanelContentType,
}: WebinarAudienceMobileSidebarProps) => {
  const webinarColors = useWebinarColors()
  const accentColor = webinarColors.accent.color
  const tabColor = webinarColors.tabs.color
  const textStyle = { color: webinarColors.tabs.highContrast.color }
  const iconColor = webinarColors.sidebar.contrast.transparent('25%')

  const { data: liveData } = useLiveDataQuery()

  const { HasWebinarActionCardsEnabled } = useFeatures()

  const audienceChatEnabled =
    liveData?.comment_mode === 'all' || liveData?.comment_mode === 'speakers'

  const [mobileTabSelected, setMobileTabSelected] = useState<MobileDeviceTab>(
    audienceChatEnabled ? 'chat' : 'other'
  )

  const activePoll = useSelector(
    (state: {
      'feature-webinar-main-sidebar-polls'?: {
        polls?: {
          open_p: string
          display_results_p: string
          poll_id: string
        }[]
      }
    }) =>
      state['feature-webinar-main-sidebar-polls']?.polls?.find(
        (poll) => intToBool(poll.open_p) || intToBool(poll.display_results_p)
      ) ?? null
  )

  const { data: activeActionCard } = useActiveActionCardsQuery()
  const prevActiveActionCard = usePrevious(activeActionCard)

  const prevActivePoll = usePrevious(activePoll)

  useEffect(() => {
    if (
      audienceChatEnabled &&
      mobileTabSelected === 'chat' &&
      activePoll &&
      activePoll.poll_id !== prevActivePoll?.poll_id
    ) {
      setMobileTabSelected('other')
    }
  }, [audienceChatEnabled, mobileTabSelected, prevActivePoll, activePoll])

  useEffect(() => {
    if (
      audienceChatEnabled &&
      mobileTabSelected === 'chat' &&
      activeActionCard &&
      activeActionCard.id !== prevActiveActionCard?.id
    ) {
      setMobileTabSelected('other')
    }
  }, [
    activeActionCard,
    audienceChatEnabled,
    mobileTabSelected,
    prevActiveActionCard?.id,
  ])

  const styles = {
    inputStyle: {
      color: webinarColors.tabs.highContrast.color,
      backgroundColor:
        webinarColors.sidebar.contrast.color === '#000000'
          ? 'rgba(0, 0, 0, 0.05)'
          : 'rgba(0, 0, 0, 0.15)',
    },
    buttonStyle: { backgroundColor: webinarColors.accent.color },
    secondaryButtonStyle: {
      backgroundColor: webinarColors.sidebar.contrast.transparent('20%'),
      color: webinarColors.accent.color,
    },
    progressBarStyles: {
      strokeColor: webinarColors.accent.color,
      trailColor: webinarColors.sidebar.color,
    },
    radioButtonStyles: {
      passiveColor: webinarColors.tabs.contrast.color,
      activeColor: webinarColors.accent.color,
    },
  }

  const renderPollUnitWrapper = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (pollProps: any) => {
      return (
        <WebinarPanelComponent
          style={{
            color: webinarColors.tabs.highContrast.color,
            backgroundColor: webinarColors.tabs.color,
          }}
          {...pollProps}
          title={
            pollProps.title && intl.formatMessage({ defaultMessage: 'Poll' })
          }
          icon={{
            ...pollProps.icon,
            color: webinarColors.sidebar.contrast.transparent('25%'),
          }}
        />
      )
    },
    [webinarColors]
  )

  const cssVars = {
    '--webinar-color-accent': webinarColors.accent.color,
    '--webinar-color-sidebar': webinarColors.sidebar.color,
    '--webinar-color-tabs': webinarColors.tabs.color,
    '--webinar-color-sidebar-text': webinarColors.sidebar.highContrast.color,
    '--webinar-color-sidebar-ctacontrast': webinarColors.sidebar.contrast.color,
    '--webinar-color-tabs-text': webinarColors.tabs.highContrast.color,
    '--webinar-color-tabs-ctacontrast': webinarColors.tabs.contrast.color,
    '--webinar-color-accent-text': webinarColors.accent.highContrast.color,
    '--webinar-color-accent-ctacontrast': webinarColors.accent.contrast.color,
    '--webinar-color-sidebarSecondaryText':
      webinarColors.tabs.highContrast.transparent('85%'),
    '--webinar-color-inputBox':
      webinarColors.sidebar.contrast.color === '#000000'
        ? 'rgba(0, 0, 0, 0.05)'
        : 'rgba(0, 0, 0, 0.15)',
  } as React.CSSProperties

  const isDigitalEvent = !!liveData?.live_series_id

  if (!liveData) return null

  return (
    <div
      className="webinar-audience-sidebar-component is-mobile-device"
      style={cssVars}
    >
      <div
        className={classnames('sidebar-main-panel-wrapper', 'is-mobile-device')}
        style={{
          backgroundColor: webinarColors.sidebar.color,
          color: webinarColors.sidebar.highContrast.color,
        }}
      >
        <div className="webinar-mobile-tab-selector-component">
          <Button
            className={classnames('webinar-mobile-tab-selector-button', {
              'webinar-mobile-tab-selector-button-selected':
                mobileTabSelected === 'chat',
            })}
            size="small"
            label={
              audienceChatEnabled
                ? intl.formatMessage({ defaultMessage: 'Chat' })
                : intl.formatMessage({ defaultMessage: 'Info' })
            }
            onClick={() => {
              setMobileTabSelected('chat')
            }}
          >
            {audienceChatEnabled ? (
              <FormattedMessage defaultMessage="Chat" />
            ) : (
              <FormattedMessage defaultMessage="Info" />
            )}
          </Button>
          <Button
            className={classnames('webinar-mobile-tab-selector-button', {
              'webinar-mobile-tab-selector-button-selected':
                mobileTabSelected === 'other',
            })}
            size="small"
            label={intl.formatMessage({ defaultMessage: 'Feedback' })}
            onClick={() => {
              setMobileTabSelected('other')
            }}
          >
            <FormattedMessage defaultMessage="Feedback" />
          </Button>
          {isDigitalEvent && (
            <Button
              className={classnames('webinar-mobile-tab-selector-button', {
                'webinar-mobile-tab-selector-button-selected':
                  mobileTabSelected === 'sessions',
              })}
              size="small"
              label={intl.formatMessage({ defaultMessage: 'Webinars' })}
              onClick={() => {
                toggleSecondaryPanel('sessions')
                setMobileTabSelected('sessions')
              }}
            >
              <FormattedMessage defaultMessage="Webinars" />
            </Button>
          )}
        </div>

        {mobileTabSelected === 'chat' && (
          <WebinarInfoAudienceSidebarComponent
            onAskQuestion={onAskQuestion}
            styles={styles}
            isMobileDevice
          />
        )}

        <div
          className={classnames('webinar-mobile-other-tab-wrapper', {
            'display-none':
              mobileTabSelected === 'chat' || mobileTabSelected === 'sessions',
          })}
        >
          {HasWebinarActionCardsEnabled && <ActionCard />}
          <PollsFeature
            styles={styles}
            iconColor={iconColor}
            accentColor={accentColor}
            objectToken={liveData.token}
            renderPollUnitWrapper={renderPollUnitWrapper}
            disableOverlayTrigger={isInGearMode}
            isPublicPollOnly
            canChangePoll={false}
            isVotingAllowed
            namespace="webinar-main-sidebar-polls"
            siblingFeatureNameSpace="webinar-secondary-sidebar-polls"
            togglePollOverlay={togglePollOverlay}
          />
          <HandoutsFeature
            namespace="webinar-sidebar-handouts"
            liveToken={liveData.token}
            style={{ ...textStyle, backgroundColor: tabColor }}
            styles={{ progressBarColor: accentColor }}
            iconColor={iconColor}
            mode="view"
          />
        </div>

        {mobileTabSelected === 'chat' && !audienceChatEnabled && (
          <WebinarInfoSidebarComponent />
        )}

        {audienceChatEnabled && mobileTabSelected === 'chat' && <Chat />}

        {mobileTabSelected === 'sessions' && secondaryPanelContentType && (
          <div className="secondary-panel-wrapper">
            <WebinarSidebarSecondaryPanelComponent>
              {
                {
                  sessions: (
                    <SessionsDrawer
                      seriesId={Number(liveData.live_series_id)}
                      seriesToken={liveData.live_series_token}
                      webinarId={Number(liveData.live_id)}
                    />
                  ),
                }[secondaryPanelContentType]
              }
            </WebinarSidebarSecondaryPanelComponent>
          </div>
        )}
      </div>
    </div>
  )
}

export { WebinarAudienceMobileSidebarComponent }
